import { setOnSessionCleared, setOnTokensStored, refreshSession } from './authSessionRefresh';
import { getAccessTokenExpiresAt, getStoredRefreshToken } from './authStorage';

/** Refresh access token this long before it expires (API default access JWT: 15m). */
export const REFRESH_BEFORE_MS = 60_000;

/** Fallback poll when `expiresIn` was not returned (legacy responses). */
const FALLBACK_TICK_MS = 30_000;

let proactiveTimeout: ReturnType<typeof setTimeout> | null = null;
let fallbackTimer: ReturnType<typeof setInterval> | null = null;

function msUntilProactiveRefresh(): number | null {
  if (!getStoredRefreshToken()) return null;
  const expiresAt = getAccessTokenExpiresAt();
  if (!expiresAt) return null;
  return Math.max(0, expiresAt - Date.now() - REFRESH_BEFORE_MS);
}

/** Schedule a single refresh ~1 minute before access token expiry. */
export function scheduleProactiveRefresh(): void {
  if (proactiveTimeout) {
    clearTimeout(proactiveTimeout);
    proactiveTimeout = null;
  }
  const delay = msUntilProactiveRefresh();
  if (delay == null) return;

  proactiveTimeout = setTimeout(() => {
    proactiveTimeout = null;
    void refreshSession()
      .then(() => scheduleProactiveRefresh())
      .catch(() => undefined);
  }, delay);
}

/** Call after login, verify-email, refresh, or any token rotation. */
export function notifyAuthTokensUpdated(): void {
  scheduleProactiveRefresh();
}

function fallbackTick(): void {
  if (!getStoredRefreshToken()) return;
  const expiresAt = getAccessTokenExpiresAt();
  if (!expiresAt) return;
  if (expiresAt - Date.now() > REFRESH_BEFORE_MS) return;
  void refreshSession()
    .then(() => scheduleProactiveRefresh())
    .catch(() => undefined);
}

export function startAuthTokenRefresh(): void {
  stopAuthTokenRefresh();
  scheduleProactiveRefresh();
  fallbackTimer = setInterval(fallbackTick, FALLBACK_TICK_MS);
}

export function stopAuthTokenRefresh(): void {
  if (proactiveTimeout) {
    clearTimeout(proactiveTimeout);
    proactiveTimeout = null;
  }
  if (fallbackTimer) {
    clearInterval(fallbackTimer);
    fallbackTimer = null;
  }
}

setOnTokensStored(() => scheduleProactiveRefresh());
setOnSessionCleared(() => stopAuthTokenRefresh());
